import { computed, inject, Injectable, signal } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { ProgrammingCapabilitiesRepository } from '@data/repositories';
import { TFilterProgramming } from '@data/types/params';
import {
  ProgrammingHierarchical,
  ProgrammingPercentage,
} from '@domain/builders';
import { List } from '@domain/helpers';
import { DateTimeAdapter } from '@domain/adapters';
import {
  LongDateTableData,
  LongDateTableSummary,
} from '@core/modules/capacities/blocs';
import {
  TUpdateProgrammingCapability,
  TUpdateProgrammingParams,
} from '@domain/types';
import {
  PROGRAMMING_INITIAL_CHUNK_DAYS,
  ProgrammingCapability,
} from '../../constants';

type TProgrammingState = {
  filters: TFilterProgramming | null;
  isFetching: ProgrammingCapability[];
  capabilitiesHierarchical: ProgrammingHierarchical[];
  capabilitiesPercentages: ProgrammingPercentage[];
  listUpdateItems: TUpdateProgrammingParams[];
};

const initialState: TProgrammingState = {
  filters: null,
  isFetching: [],
  capabilitiesHierarchical: [],
  capabilitiesPercentages: [],
  listUpdateItems: [],
};

@Injectable({
  providedIn: 'root',
})
export class ProgrammingProvider {
  readonly #programmingRepository = inject(ProgrammingCapabilitiesRepository);

  readonly #state = signalState(initialState);

  readonly hierarchical = this.#state.capabilitiesHierarchical;
  readonly percentages = this.#state.capabilitiesPercentages;
  readonly listUpdateItems = this.#state.listUpdateItems;

  /**
   la tabla de detalle se carga despues que se carguen todos los registros principales
   */
  readonly #isLoadingMore = signal(false);
  readonly isLoadingMore = this.#isLoadingMore.asReadonly();

  readonly isFetchingCapabilities = computed(
    () =>
      !!this.#state
        .isFetching()
        ?.some((key) =>
          [
            ProgrammingCapability.IS_FETCHING_HIERARCHICAL,
            ProgrammingCapability.IS_FETCHING_PERCENTAGE,
          ].includes(key),
        ),
  );

  readonly isFetchingValidation = computed(() =>
    this.#state
      .isFetching()
      .includes(ProgrammingCapability.IS_FETCHING_VALIDATE),
  );

  readonly isFetchingUpdate = computed(() =>
    this.#state.isFetching().includes(ProgrammingCapability.IS_FETCHING_UPDATE),
  );

  #lastTableData: LongDateTableData | null = null;

  /**
   * Cuando un bloque de fondo anexa fechas (misma búsqueda, array que
   * crece a partir del mismo primer elemento), se reutiliza el mapa ya
   * construido por la instancia anterior en vez de reprocesar todo —
   * ver LongDateTableData para el detalle del procesamiento incremental.
   * Una búsqueda nueva reemplaza el array por uno distinto y no aplica.
   */
  readonly longDateTableData = computed(() => {
    const data = this.hierarchical();
    const isSameSearchAppended =
      !!this.#lastTableData?.data.length &&
      data[0] === this.#lastTableData.data[0];

    const instance = new LongDateTableData(
      data,
      isSameSearchAppended ? this.#lastTableData : undefined,
    );
    this.#lastTableData = instance;

    return instance;
  });

  readonly longDateTableSummary = computed(
    () => new LongDateTableSummary(this.percentages()),
  );

  setLoading(key: ProgrammingCapability) {
    patchState(this.#state, ({ isFetching }) => ({
      isFetching: List(isFetching).add(key),
    }));
  }

  setFilters(filters: TFilterProgramming | null) {
    patchState(this.#state, { filters });
  }

  removeLoading(key: ProgrammingCapability) {
    patchState(this.#state, ({ isFetching }) => ({
      isFetching: List(isFetching).remove(key),
    }));
  }

  /**
   * `trackLoading` en false evita tocar el flag global de "cargando":
   * lo usa la carga de fondo (el resto del rango tras el primer chunk)
   * para no volver a ocultar la tabla que ya está visible con datos.
   */
  async getProgrammingHierarchical(
    params: TFilterProgramming,
    append = false,
    trackLoading = true,
  ) {
    const key = ProgrammingCapability.IS_FETCHING_HIERARCHICAL;
    if (trackLoading) this.setLoading(key);

    const result =
      await this.#programmingRepository.getProgrammingHierarchical(params);

    if (trackLoading) this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    patchState(this.#state, ({ capabilitiesHierarchical }) => ({
      capabilitiesHierarchical: append
        ? [...capabilitiesHierarchical, ...result.data]
        : result.data,
    }));
  }

  async getProgrammingPercentages(
    params: TFilterProgramming,
    append = false,
    trackLoading = true,
  ) {
    const key = ProgrammingCapability.IS_FETCHING_PERCENTAGE;
    if (trackLoading) this.setLoading(key);

    const result =
      await this.#programmingRepository.getProgrammingPercentages(params);

    if (trackLoading) this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    patchState(this.#state, ({ capabilitiesPercentages }) => ({
      capabilitiesPercentages: append
        ? [...capabilitiesPercentages, ...result.data]
        : result.data,
    }));
  }

  /**
   * Trae primero los PROGRAMMING_INITIAL_CHUNK_DAYS días para que la tabla se vea
   * rápido, y si el rango pedido es más largo, trae el resto en bloques
   * del mismo tamaño, uno por uno en segundo plano, anexando cada uno a
   * medida que llega (en vez de un solo bloque grande que vuelve a
   * sentirse lento). No toca el loader que oculta la tabla
   * (trackLoading=false) para no esconderla entre bloques.
   */
  async getProgrammingData(params: TFilterProgramming) {
    patchState(this.#state, { filters: params });

    const [firstChunk, ...restChunks] = this.#splitIntoChunks(params);

    const firstResult = await Promise.allSettled([
      this.getProgrammingHierarchical(firstChunk),
      this.getProgrammingPercentages(firstChunk),
    ]);

    if (restChunks.length) {
      this.#isLoadingMore.set(true);
      this.#loadChunksInBackground(restChunks).finally(() => {
        this.#isLoadingMore.set(false);
      });
    } else {
      this.#isLoadingMore.set(false);
    }

    return firstResult;
  }

  async #loadChunksInBackground(chunks: TFilterProgramming[]) {
    for (const chunk of chunks) {
      await Promise.allSettled([
        this.getProgrammingHierarchical(chunk, true, false),
        this.getProgrammingPercentages(chunk, true, false),
      ]);
    }
  }

  #splitIntoChunks(params: TFilterProgramming): TFilterProgramming[] {
    const chunks: TFilterProgramming[] = [];
    let currentStart = params.start_date;

    while (currentStart <= params.end_date) {
      const chunkEnd = DateTimeAdapter.addDays(
        currentStart,
        PROGRAMMING_INITIAL_CHUNK_DAYS - 1,
      );
      const boundedEnd =
        chunkEnd < params.end_date ? chunkEnd : params.end_date;

      chunks.push({ ...params, start_date: currentStart, end_date: boundedEnd });

      if (boundedEnd >= params.end_date) break;
      currentStart = DateTimeAdapter.addDays(boundedEnd, 1);
    }

    return chunks;
  }

  async validateCapability(payload: TUpdateProgrammingParams) {
    const key = ProgrammingCapability.IS_FETCHING_VALIDATE;
    this.setLoading(key);

    const formData: TUpdateProgrammingCapability = {
      ...payload,
      filialId: this.#state.filters()?.filial,
      operationId: this.#state.filters()?.operation,
    };

    const result =
      await this.#programmingRepository.validateCapabilityAvailable(formData);

    this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    return result.data;
  }

  async updateCapabilities(payload: TUpdateProgrammingParams[]) {
    const key = ProgrammingCapability.IS_FETCHING_UPDATE;
    this.setLoading(key);

    const formData: TUpdateProgrammingCapability[] = payload.map((item) => ({
      ...item,
      filialId: this.#state.filters()?.filial,
      operationId: this.#state.filters()?.operation,
    }));

    const result =
      await this.#programmingRepository.updateMultipleCapability(formData);

    this.removeLoading(key);

    return result;
  }

  async handleUpdateCapabilities() {
    const listItems = this.#state.listUpdateItems();
    const filters = this.#state.filters();

    const result = await this.updateCapabilities(listItems);

    if (result.isOk === false) {
      throw result.error;
    }

    if (result.data.success === false) {
      return result.data;
    }

    this.clearUpdateItem();

    const resultData = await this.getProgrammingData(filters);

    const error = resultData?.find((item) => item.status === 'rejected');

    if (error) {
      throw new Error(error.reason);
    }

    return result.data;
  }

  addUpdateItem(item: TUpdateProgrammingParams) {
    patchState(this.#state, ({ listUpdateItems }) => {
      const newListItems = listUpdateItems.filter((lui) =>
        [
          lui.bagId !== item.bagId,
          lui.capabilityDate !== item.capabilityDate,
          lui.segmentedZoneId !== item.segmentedZoneId,
          lui.timeFrameId !== item.timeFrameId,
        ].some(Boolean),
      );

      if (newListItems.length === listUpdateItems.length) {
        return {
          listUpdateItems: [...listUpdateItems, item],
        };
      }

      return {
        listUpdateItems: [...newListItems, item],
      };
    });
  }

  clearUpdateItem() {
    patchState(this.#state, {
      listUpdateItems: [],
    });
  }

  async clear() {
    patchState(this.#state, initialState);
  }
}
